/**
 * 학습 설정 에디터
 */
import type { LearningSettings, LearningSettingsOverrides } from '@/types/configuration'

interface LearningSettingsEditorProps {
  settings: LearningSettings
  onUpdate: (settings: LearningSettings) => void
  overrides?: LearningSettingsOverrides
  onOverridesUpdate?: (overrides: LearningSettingsOverrides) => void
}

export const LearningSettingsEditor: React.FC<LearningSettingsEditorProps> = ({
  settings,
  onUpdate,
  overrides,
  onOverridesUpdate
}) => {
  // 하위 변경 가능 토글
  const renderOverridableToggle = (field: keyof LearningSettingsOverrides) => {
    if (!onOverridesUpdate) return null
    const isOverridable = overrides?.[field] !== false
    return (
      <label className="flex items-center gap-1.5 ml-auto cursor-pointer" title={isOverridable ? '하위에서 변경 가능' : '하위에서 변경 불가'}>
        <input
          type="checkbox"
          checked={isOverridable}
          onChange={(e) => onOverridesUpdate({ ...overrides, [field]: e.target.checked })}
          className="w-3 h-3 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
        />
        <span className={`text-xs ${isOverridable ? 'text-purple-600' : 'text-gray-400'}`}>
          하위 변경
        </span>
      </label>
    )
  }

  return (
    <div className="space-y-6">
      <h4 className="text-sm font-medium text-gray-700">학습 환경 설정</h4>

      <div className="space-y-4">
        {/* 대시보드 표시 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.showDashboard}
            onChange={(e) => onUpdate({ ...settings, showDashboard: e.target.checked })}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
              학습 대시보드 표시
              {renderOverridableToggle('showDashboard')}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              레슨 시작 시 Pre-Quiz, Video, Post-Quiz 카드 형태의 대시보드를 표시합니다.
            </p>
          </div>
        </label>

        {/* 진행률 표시 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.showProgress}
            onChange={(e) => onUpdate({ ...settings, showProgress: e.target.checked })}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
              진행률 표시
              {renderOverridableToggle('showProgress')}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              학습 화면에 현재 진행률을 표시합니다.
            </p>
          </div>
        </label>

        {/* 학습 시간 추적 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.trackLearningTime}
            onChange={(e) => onUpdate({ ...settings, trackLearningTime: e.target.checked })}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
              학습 시간 추적
              {renderOverridableToggle('trackLearningTime')}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              학습자가 각 컴포넌트에서 소요한 시간을 추적하고 기록합니다.
            </p>
          </div>
        </label>

        {/* 노트 기능 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enableNotes}
            onChange={(e) => onUpdate({ ...settings, enableNotes: e.target.checked })}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
              노트/메모 기능
              {renderOverridableToggle('enableNotes')}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              학습 중 개인 노트를 작성할 수 있는 기능을 제공합니다.
            </p>
          </div>
        </label>

        {/* 북마크 기능 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enableBookmarks}
            onChange={(e) => onUpdate({ ...settings, enableBookmarks: e.target.checked })}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
              북마크 기능
              {renderOverridableToggle('enableBookmarks')}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              비디오의 특정 시점이나 중요한 부분을 북마크할 수 있습니다.
            </p>
          </div>
        </label>
      </div>

      {/* 설명 박스 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 mb-2">설정 적용 범위</h5>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• 이 설정은 학습자의 학습 환경에 적용됩니다.</li>
          <li>• 대시보드가 비활성화되면 바로 첫 번째 컴포넌트로 진입합니다.</li>
          <li>• 노트와 북마크 데이터는 학습자별로 저장됩니다.</li>
        </ul>
      </div>
    </div>
  )
}
